// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  vendor/src/pages/Orders.js  — Order History
//  Updated: proper status tabs + grocery support
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import API from '../api/config';
import toast from 'react-hot-toast';
import {
  Search, ChevronLeft, ChevronRight,
  X, User, Phone, MapPin, Banknote, Calendar,
  Package, Clock, ShoppingBag, UtensilsCrossed, ShoppingCart, Bike,
} from 'lucide-react';

const STATUS_CONFIG = {
  placed:       { color: '#d97706', bg: '#fef3c7', label: 'Placed'      },
  accepted:     { color: '#2563eb', bg: '#dbeafe', label: 'Accepted'    },
  preparing:    { color: '#7c3aed', bg: '#ede9fe', label: 'Preparing'   },
  packing:      { color: '#7c3aed', bg: '#ede9fe', label: 'Packing'     },
  ready:        { color: '#059669', bg: '#d1fae5', label: 'Ready'       },
  packed:       { color: '#059669', bg: '#d1fae5', label: 'Packed'      },
  picked:       { color: '#ea580c', bg: '#fff7ed', label: 'Picked Up'   },
  'on-the-way': { color: '#ea580c', bg: '#fff7ed', label: 'On the Way'  },
  delivered:    { color: '#059669', bg: '#d1fae5', label: 'Delivered'   },
  cancelled:    { color: '#dc2626', bg: '#fee2e2', label: 'Cancelled'   },
};

const TABS = [
  { key: 'all',       label: 'All'       },
  { key: 'placed',    label: 'New'       },
  { key: 'accepted',  label: 'Accepted'  },
  { key: 'preparing', label: 'Preparing' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'cancelled', label: 'Cancelled' },
];

export default function Orders() {
  const { vendor } = useAuth();
  const [orders,        setOrders]        = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filter,        setFilter]        = useState('all');
  const [search,        setSearch]        = useState('');
  const [page,          setPage]          = useState(1);
  const [totalPages,    setTotalPages]    = useState(1);
  const [total,         setTotal]         = useState(0);

  const vd          = vendor?.vendor || vendor;
  const isGrocery   = (vd?.businessType || '').toLowerCase() === 'grocery';
  const vendorEmail = vendor?.email || vendor?.vendor?.email;

  const fetchOrders = async () => {
    if (!vendorEmail) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        vendorEmail, page, limit: 20,
        ...(filter !== 'all' ? { status: filter } : {}),
      });
      const res = await API.get(`/orders/vendor/all?${params}`);
      setOrders(res.data.orders || []);
      setTotalPages(res.data.totalPages || 1);
      setTotal(res.data.total || 0);
    } catch { toast.error('Orders load nahi hue!'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchOrders(); }, [vendorEmail, page, filter]);

  const filtered = orders.filter(o => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      o._id.slice(-6).toLowerCase().includes(s) ||
      (o.user?.name || o.customer?.name || '').toLowerCase().includes(s) ||
      (o.orderId || '').toLowerCase().includes(s)
    );
  });

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div style={S.title}>Order History</div>
        <div style={S.sub}>{total} total orders</div>
      </div>

      {/* Search */}
      <div style={S.search}>
        <Search size={16} color='#bbb' />
        <input
          style={S.searchInput}
          placeholder='Order ID ya customer naam...'
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={15} color='#bbb' />
          </button>
        )}
      </div>

      {/* Filter tabs */}
      <div style={S.tabs}>
        {TABS.map(f => (
          <button key={f.key}
            style={{ ...S.tab, ...(filter === f.key ? S.tabActive : {}) }}
            onClick={() => { setFilter(f.key); setPage(1); }}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={S.empty}>
          <Clock size={28} color='#ff6b35' style={{ animation: 'spin 1s linear infinite' }} />
          <p style={{ marginTop: 12, color: '#888' }}>Load ho raha hai...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div style={S.empty}>
          <ShoppingBag size={48} color='#e0e0e0' />
          <p style={{ marginTop: 14, color: '#888' }}>Koi order nahi mila</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(order => {
            const customer = order.user || order.customer;
            const amount   = order.total || order.totalAmount || 0;
            const sc       = STATUS_CONFIG[order.status] || { color: '#888', bg: '#f3f4f6', label: order.status };
            const grocery  = order.serviceType === 'grocery' || !!order.store;

            return (
              <div key={order._id} style={S.row} onClick={() => setSelectedOrder(order)}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {grocery
                      ? <ShoppingCart size={14} color='#ff6b35' />
                      : <UtensilsCrossed size={14} color='#ff6b35' />}
                    <span style={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a' }}>
                      #{order._id.slice(-6).toUpperCase()}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <User size={13} color='#bbb' />
                    <span style={{ fontSize: 13, color: '#555' }}>{customer?.name || 'Customer'}</span>
                  </div>
                  <span style={{ fontSize: 12, color: '#bbb' }}>
                    {order.items?.length} items · {order.paymentMode?.toUpperCase() || 'N/A'}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5 }}>
                  <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: sc.bg, color: sc.color }}>
                    {sc.label}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Calendar size={11} color='#ccc' />
                    <span style={{ fontSize: 11, color: '#ccc' }}>
                      {new Date(order.createdAt).toLocaleDateString('en-IN')}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 14, marginTop: 20 }}>
          <button style={S.pageBtn} disabled={page === 1} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft size={16} color='#fff' />
          </button>
          <span style={{ fontSize: 13, color: '#666' }}>Page {page} / {totalPages}</span>
          <button style={S.pageBtn} disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
            <ChevronRight size={16} color='#fff' />
          </button>
        </div>
      )}

      {selectedOrder && (
        <OrderModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function OrderModal({ order, onClose }) {
  const customer = order.user || order.customer;
  const amount   = order.total || order.totalAmount || 0;
  const sc       = STATUS_CONFIG[order.status] || { color: '#888', bg: '#f3f4f6', label: order.status };
  const isGro    = order.serviceType === 'grocery' || !!order.store;

  return (
    <div style={M.overlay} onClick={onClose}>
      <div style={M.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={M.header}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 18, color: '#1a1a1a', marginBottom: 6 }}>
              #{order._id.slice(-6).toUpperCase()}
              {isGro && (
                <span style={{ fontSize: 11, fontWeight: 700, background: '#eff6ff', color: '#2563eb', padding: '2px 8px', borderRadius: 6, marginLeft: 8 }}>
                  GROCERY
                </span>
              )}
            </div>
            <span style={{ padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: sc.bg, color: sc.color }}>
              {sc.label}
            </span>
          </div>
          <button style={M.close} onClick={onClose}><X size={18} color='#888' /></button>
        </div>

        <div style={M.body}>
          {/* Info rows */}
          {[
            { icon: <User size={15} color='#bbb' />,     label: 'Customer',    value: customer?.name },
            { icon: <Phone size={15} color='#bbb' />,    label: 'Phone',       value: customer?.phone || 'N/A' },
            { icon: <Banknote size={15} color='#bbb' />, label: 'Payment',     value: order.paymentMode?.toUpperCase() },
            { icon: <Calendar size={15} color='#bbb' />, label: 'Date',        value: new Date(order.createdAt).toLocaleString('en-IN') },
          ].map((r, i) => (
            <div key={i} style={M.infoRow}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{r.icon}<span style={M.label}>{r.label}</span></div>
              <span style={M.val}>{r.value || '—'}</span>
            </div>
          ))}

          {/* Rider info */}
          {order.rider?.name && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff7ed', borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}>
              <Bike size={14} color='#f97316' />
              <div>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>Rider</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1c1c1e' }}>
                  {order.rider.name}
                  {order.rider.phone && (
                    <a href={`tel:${order.rider.phone}`} style={{ marginLeft: 8, color: '#f97316', fontSize: 12 }}>
                      {order.rider.phone}
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Address */}
          {order.deliveryAddress && (
            <div style={M.addr}>
              <MapPin size={14} color='#bbb' />
              <span style={{ fontSize: 13, color: '#555', lineHeight: 1.5 }}>
                {[order.deliveryAddress.line1, order.deliveryAddress.area, order.deliveryAddress.city]
                  .filter(Boolean).join(', ') || JSON.stringify(order.deliveryAddress)}
              </span>
            </div>
          )}

          <div style={M.divider} />

          {/* Items */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontWeight: 700, fontSize: 14, color: '#1a1a1a' }}>
            <Package size={15} color='#ff6b35' /> Items
          </div>
          {(order.items || []).map((item, i) => (
            <div key={i} style={M.item}>
              <span style={{ fontSize: 13, color: '#444', flex: 1 }}>{item.name}</span>
              <span style={{ fontSize: 13, color: '#888' }}>×{item.qty || item.quantity || 1}</span>
            </div>
          ))}


        </div>
      </div>
    </div>
  );
}

const S = {
  page:        { padding: '16px', paddingBottom: 80, background: '#f8f8f8', minHeight: '100vh' },
  header:      { marginBottom: 16 },
  title:       { fontSize: 22, fontWeight: 800, color: '#1a1a1a' },
  sub:         { fontSize: 13, color: '#888', marginTop: 3 },
  search:      { display: 'flex', alignItems: 'center', gap: 10, background: '#fff', borderRadius: 14, padding: '12px 16px', marginBottom: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' },
  searchInput: { flex: 1, fontSize: 14, color: '#1a1a1a', background: 'none', border: 'none', outline: 'none' },
  tabs:        { display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' },
  tab:         { padding: '7px 16px', borderRadius: 20, background: '#f0f0f0', color: '#666', fontSize: 13, fontWeight: 500, border: 'none', cursor: 'pointer' },
  tabActive:   { background: '#ff6b35', color: '#fff' },
  row:         { background: '#fff', borderRadius: 14, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', cursor: 'pointer', border: '1px solid #f5f5f5' },
  empty:       { textAlign: 'center', padding: '60px 20px' },
  pageBtn:     { width: 36, height: 36, borderRadius: 10, background: '#ff6b35', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer' },
};

const M = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 },
  modal:   { background: '#fff', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 500, maxHeight: '90vh', overflowY: 'auto' },
  header:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '20px', borderBottom: '1px solid #f0f0f0', position: 'sticky', top: 0, background: '#fff', zIndex: 1 },
  close:   { width: 34, height: 34, borderRadius: '50%', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer' },
  body:    { padding: '20px' },
  infoRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  label:   { fontSize: 13, color: '#888' },
  val:     { fontSize: 13, color: '#1a1a1a', fontWeight: 500 },
  addr:    { display: 'flex', alignItems: 'flex-start', gap: 8, background: '#f9f9f9', padding: '10px 12px', borderRadius: 10, marginBottom: 12 },
  divider: { height: 1, background: '#f5f5f5', margin: '14px 0' },
  item:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px dashed #f5f5f5' },
};